// Semaine, menus & recettes — éditeur des 4 semaines.
//
// Un réservoir de choix par repas, partagé par les 4 semaines : chaque
// semaine désigne son plat principal, les autres choix restent ses
// alternatives. Toute modification passe par le service pur weekMenus
// (recettes déplacées avec le titre, descriptions conservées) puis par
// l'enregistrement confirmé de l'aperçu. Enregistrer n'est jamais valider.

import { useEffect, useMemo, useState } from "react";
import Icon from "./Icon";
import {
  AUTOMATIC_CHECK_NOTE,
  PROSE_EXCLUSIONS_CONFIRMATION,
  WEEK_MENU_STATUS,
  WEEK_MENU_STATUS_LABELS,
  WEEK_MENUS_LIMITS,
  WEEK_MENUS_MIN_ALTERNATIVES,
  addAlternative,
  buildWeekMenusModel,
  confirmFoodConflict,
  confirmProseExclusionsChecked,
  describeClientConstraints,
  describeWeekMenuChange,
  foodConflictConfirmationLabel,
  listReusableRecipes,
  markWeekMenusSaved,
  mergeWeekMenusDraft,
  removeAlternative,
  readableMealTitle,
  renameChoice,
  reuseRecipe,
  runWeekMenusOps,
  setHint,
  setRecipe,
  setWeekMain,
  validateSlot,
} from "../services/weekMenus";

const WEEK_MENUS_SAVED_NOTICE = WEEK_MENU_STATUS_LABELS.saved_to_validate;
const VARYING_MESSAGE = "Ce repas varie selon les jours dans le plan. Le modifier ici appliquera le même réservoir aux 7 jours.";
const STALE_AUTHORITY_MESSAGE = "Les recettes ont été modifiées ailleurs — reprends ces menus";
const KEPT_DRAFT_MESSAGE = "Une autre section a été enregistrée ; tes modifications de menus sont conservées, enregistre-les.";
const SOURCE_RETURN_NOTE = "Les menus de l’éditeur redeviennent la source";
const PENDING_RECIPE_MESSAGE ="La recette ouverte sera appliquée à l’enregistrement.";

const STATUS_COLORS = {
  [WEEK_MENU_STATUS.INCOMPLETE]: { color: "#e5c878", border: "rgba(220, 180, 80, 0.30)", background: "rgba(220, 180, 80, 0.12)" },
  [WEEK_MENU_STATUS.SAVED_TO_VALIDATE]: { color: "#cfcfc4", border: "rgba(212, 201, 168, 0.30)", background: "rgba(212, 201, 168, 0.10)" },
  [WEEK_MENU_STATUS.COMPLETE_TO_VALIDATE]: { color: "#d4c9a8", border: "rgba(212, 201, 168, 0.30)", background: "rgba(212, 201, 168, 0.08)" },
  [WEEK_MENU_STATUS.VALIDATED]: { color: "#a8e0c0", border: "rgba(106, 191, 138, 0.35)", background: "rgba(106, 191, 138, 0.12)" },
};

const cardStyle = {
  background: "rgba(255, 255, 255, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: 12,
  overflow: "hidden",
};
const headerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 16px",
  background: "rgba(212, 201, 168, 0.04)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
  flexWrap: "wrap",
};
const blockStyle = {
  background: "rgba(0,0,0,0.12)",
  border: "1px solid rgba(212, 201, 168, 0.12)",
  borderRadius: 8,
  padding: 12,
  minWidth: 0,
};
const buttonStyle = {
  minHeight: 44,
  borderRadius: 7,
  border: "1px solid rgba(212, 201, 168, 0.20)",
  background: "rgba(212, 201, 168, 0.08)",
  color: "#d4c9a8",
  fontFamily: "inherit",
  fontSize: ".78rem",
  fontWeight: 600,
  padding: "8px 12px",
  cursor: "pointer",
};
const quietButtonStyle = {
  ...buttonStyle,
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#8a8a7a",
  fontWeight: 500,
};
const validateButtonStyle = {
  ...buttonStyle,
  border: "1px solid rgba(106, 191, 138, 0.35)",
  background: "rgba(106, 191, 138, 0.15)",
  color: "#a8e0c0",
};
const inputStyle = {
  width: "100%",
  minHeight: 44,
  boxSizing: "border-box",
  padding: "8px 10px",
  background: "rgba(0,0,0,0.18)",
  border: "1px solid rgba(212, 201, 168, 0.18)",
  borderRadius: 6,
  color: "#f0f0e8",
  fontFamily: "inherit",
  fontSize: ".85rem",
};
const labelStyle = {
  display: "block",
  marginBottom: 4,
  color: "#8a8a7a",
  fontSize: ".68rem",
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
};

const RESPONSIVE_CSS = `
.week-menus__tabs { display: flex; gap: 8px; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
.week-menus__tab { flex: 0 0 auto; }
.week-menus__blocks { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
@media (max-width: 720px) {
  .week-menus__blocks { grid-template-columns: 1fr; }
  .week-menus__actions { flex-direction: column; align-items: stretch; }
}
`;

function StatusChip({ status, label }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS[WEEK_MENU_STATUS.INCOMPLETE];
  return (
    <span
      className={`week-menus__chip week-menus__chip--${status}`}
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        color: colors.color,
        fontSize: ".68rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >{label || WEEK_MENU_STATUS_LABELS[status]}</span>
  );
}

function linesText(lines) {
  return Array.isArray(lines) ? lines.join("\n") : "";
}

function contentSignature(consultation) {
  const overrides = consultation?.editorial_overrides || {};
  return JSON.stringify({
    meals: overrides.meals || null,
    recipes: consultation?.meal_recipes || null,
    rotation: overrides.four_week_rotation || null,
  });
}


function editorValuesFor(choice) {
  return {
    title: choice.title,
    hint: choice.hint || "",
    ingredients: linesText(choice.recipe?.ingredients),
    preparation: linesText(choice.recipe?.preparation),
    tip: choice.recipe?.tip || "",
  };
}

// Éditeur contrôlé par le parent : un enregistrement global peut ainsi
// appliquer ce qui est en cours de saisie au lieu de le perdre.
function ChoiceEditor({ slot, choice, values, onChange, reusable, disabled, onApply, onReuse, onClose }) {
  const { title, hint, ingredients, preparation, tip } = values;
  const setTitle = (value) => onChange({ ...values, title: value });
  const setHintText = (value) => onChange({ ...values, hint: value });
  const setIngredients = (value) => onChange({ ...values, ingredients: value });
  const setPreparation = (value) => onChange({ ...values, preparation: value });
  const setTip = (value) => onChange({ ...values, tip: value });
  const idBase = `week-menus-${slot}-${choice.id}`;
  const otherRecipes = reusable.filter((item) => item.key !== choice.recipeKey);
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(212, 201, 168, 0.10)" }}>
      <div>
        <label htmlFor={`${idBase}-title`} style={labelStyle}>Titre du plat</label>
        <input id={`${idBase}-title`} type="text" value={title} maxLength={WEEK_MENUS_LIMITS.title}
          onChange={(event) => setTitle(event.target.value)} disabled={disabled} style={inputStyle} />
        {readableMealTitle(title) !== title.replace(/\s+/g, " ").trim() && (
          <button
            type="button"
            disabled={disabled}
            style={{ ...quietButtonStyle, marginTop: 6 }}
            onClick={() => onChange({
              ...values,
              title: readableMealTitle(title),
              // La ligne complète du plan n'est pas perdue : elle devient la description.
              hint: hint.trim() ? hint : title.replace(/\s+/g, " ").trim().slice(0, WEEK_MENUS_LIMITS.hint),
            })}
          >
            Raccourcir le titre : « {readableMealTitle(title)} » (la ligne complète passe en description)
          </button>
        )}
      </div>
      <div>
        <label htmlFor={`${idBase}-hint`} style={labelStyle}>Description courte (facultatif)</label>
        <input id={`${idBase}-hint`} type="text" value={hint} maxLength={WEEK_MENUS_LIMITS.hint}
          onChange={(event) => setHintText(event.target.value)} disabled={disabled} style={inputStyle} />
      </div>
      <div>
        <label htmlFor={`${idBase}-ingredients`} style={labelStyle}>Ingrédients — un par ligne, avec quantité et unité</label>
        <textarea id={`${idBase}-ingredients`} rows={5} value={ingredients}
          placeholder="120 g de flocons d’avoine"
          onChange={(event) => setIngredients(event.target.value)} disabled={disabled}
          style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      <div>
        <label htmlFor={`${idBase}-preparation`} style={labelStyle}>Préparation — une étape par ligne</label>
        <textarea id={`${idBase}-preparation`} rows={4} value={preparation}
          onChange={(event) => setPreparation(event.target.value)} disabled={disabled}
          style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      <div>
        <label htmlFor={`${idBase}-tip`} style={labelStyle}>Consignes ou astuce (facultatif)</label>
        <textarea id={`${idBase}-tip`} rows={2} value={tip} maxLength={WEEK_MENUS_LIMITS.tip}
          onChange={(event) => setTip(event.target.value)} disabled={disabled}
          style={{ ...inputStyle, resize: "vertical" }} />
      </div>
      {otherRecipes.length > 0 && (
        <div>
          <label htmlFor={`${idBase}-reuse`} style={labelStyle}>Réutiliser une recette existante</label>
          <select id={`${idBase}-reuse`} value="" disabled={disabled}
            onChange={(event) => { if (event.target.value) onReuse(event.target.value); }}
            style={{ ...inputStyle, background: "#20201f" }}>
            <option value="">Choisir une recette à copier…</option>
            {otherRecipes.map((item) => (
              <option key={item.key} value={item.key}>{item.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="week-menus__actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button type="button" onClick={onClose} disabled={disabled} style={quietButtonStyle}>Fermer</button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onApply({ title, hint, ingredients, preparation, tip })}
          style={buttonStyle}
        >Appliquer au brouillon</button>
      </div>
    </div>
  );
}

export default function WeekMenusEditor({
  client = null,
  consultation,
  weekMeals = null,
  onSave,
  onDirtyChange,
  onOpenRotation,
  disabled = false,
  authorityStale = false,
  alreadyPublished = false,
  reclaimsSource = false,
}) {
  const [draft, setDraft] = useState(consultation);
  const [syncedFrom, setSyncedFrom] = useState(consultation);
  const [keptDraftNotice, setKeptDraftNotice] = useState(false);
  const [activeWeek, setActiveWeek] = useState(1);
  const [openChoice, setOpenChoice] = useState(null);
  const [openValues, setOpenValues] = useState(null);
  const [newAlternative, setNewAlternative] = useState({});
  const [error, setError] = useState(null);
  const [errorIssues, setErrorIssues] = useState([]);
  const [notice, setNotice] = useState(null);
  const [savedChanges, setSavedChanges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmedVarying, setConfirmedVarying] = useState(() => new Set());
  const [pendingVarying, setPendingVarying] = useState(null);
  const [mergeConflicts, setMergeConflicts] = useState([]);

  // Une autre section enregistrée : la consultation change. Sans menus en
  // cours on se resynchronise ; avec des menus en cours, on les garde,
  // reposés sur la version reçue (les autres sections ne sont jamais écrasées).
  if (syncedFrom !== consultation) {
    setSyncedFrom(consultation);
    const ownSave = contentSignature(draft) === contentSignature(consultation);
    const hadEdits = contentSignature(draft) !== contentSignature(syncedFrom);
    if (ownSave || !hadEdits) {
      setDraft(consultation);
      setKeptDraftNotice(false);
      setMergeConflicts([]);
      // Les champs d'une recette ouverte décrivaient l'ancienne version.
      setOpenChoice(null);
      setOpenValues(null);
    } else {
      const merged = mergeWeekMenusDraft(syncedFrom, draft, consultation);
      setDraft(merged.consultation);
      setMergeConflicts(merged.conflicts);
      setKeptDraftNotice(true);
    }
  }

  const dirty = contentSignature(draft) !== contentSignature(consultation);
  useEffect(() => {
    if (typeof onDirtyChange !== "function") return undefined;
    onDirtyChange("week_menus", dirty);
    return () => onDirtyChange("week_menus", false);
  }, [dirty, onDirtyChange]);

  const model = useMemo(() => buildWeekMenusModel({
    client,
    consultation: draft,
    weekMeals: dirty ? null : weekMeals,
    alreadyPublished,
  }), [alreadyPublished, client, draft, dirty, weekMeals]);
  const pendingChanges = useMemo(() => (dirty
    ? markWeekMenusSaved({ client, previous: consultation, next: draft }).changes
    : []), [client, consultation, draft, dirty]);
  const reusable = useMemo(() => listReusableRecipes(model), [model]);
  const constraints = useMemo(() => describeClientConstraints(client), [client]);

  const canEdit = typeof onSave === "function" && !disabled && !saving;
  const week = model.weeks[activeWeek - 1] || model.weeks[0];

  function showError(result) {
    setError(result?.error || "Modification impossible.");
    setErrorIssues(Array.isArray(result?.issues) ? result.issues : []);
  }

  // Repas qui varie selon les jours : la première modification exige une
  // confirmation explicite, car elle imposera le même réservoir aux 7 jours.
  function needsVaryingConfirmation(slot) {
    const slotModel = model.slots.find((item) => item.slot === slot);
    return Boolean(slotModel?.variesByDay) && !confirmedVarying.has(slot);
  }

  function run(slot, operations, { onDone } = {}) {
    if (needsVaryingConfirmation(slot)) {
      setPendingVarying({ slot, operations, onDone });
      return false;
    }
    const result = runWeekMenusOps({ client, consultation: draft }, operations);
    if (!result.ok) {
      showError(result);
      return false;
    }
    setDraft(result.consultation);
    setError(null);
    setErrorIssues([]);
    setNotice(result.notices?.length ? result.notices.join(" ") : null);
    onDone?.();
    return true;
  }

  function confirmVarying() {
    if (!pendingVarying) return;
    const { slot, operations, onDone } = pendingVarying;
    setConfirmedVarying((current) => new Set([...current, slot]));
    setPendingVarying(null);
    const result = runWeekMenusOps({ client, consultation: draft }, operations);
    if (!result.ok) {
      showError(result);
      return;
    }
    setDraft(result.consultation);
    setError(null);
    setErrorIssues([]);
    setNotice(result.notices?.length ? result.notices.join(" ") : null);
    onDone?.(result.consultation);
  }

  function choiceOperations(slot, choice, values) {
    // Description et recette d'abord, renommage en dernier : le renommage
    // déplace la recette et change l'identifiant de l'alternative.
    return [
      (state) => setHint(state, slot, choice.id, values.hint),
      (state) => setRecipe(state, slot, choice.id, {
        ingredients: values.ingredients,
        preparation: values.preparation,
        tip: values.tip,
      }),
      (state) => renameChoice(state, slot, choice.id, values.title),
    ];
  }

  function openEditor(slot, choice) {
    const key = `${slot}:${choice.id}`;
    if (openChoice === key) {
      closeEditor();
      return;
    }
    setOpenChoice(key);
    setOpenValues(editorValuesFor(choice));
  }

  function closeEditor() {
    setOpenChoice(null);
    setOpenValues(null);
  }

  function openEditorTarget() {
    if (!openChoice || !openValues) return null;
    const separator = openChoice.indexOf(":");
    const slot = openChoice.slice(0, separator);
    const choiceId = openChoice.slice(separator + 1);
    const choice = model.slots.find((item) => item.slot === slot)?.choices.find((item) => item.id === choiceId);
    if (!choice) return null;
    const pending = JSON.stringify(openValues) !== JSON.stringify(editorValuesFor(choice));
    return { slot, choice, pending };
  }

  const openTarget = openEditorTarget();
  const hasPendingRecipe = Boolean(openTarget?.pending);

  function applyChoice(slot, choice, values) {
    run(slot, choiceOperations(slot, choice, values), { onDone: closeEditor });
  }

  function addAlt(slot) {
    const title = newAlternative[slot] || "";
    run(slot, [(state) => addAlternative(state, slot, title)], {
      onDone: () => setNewAlternative((current) => ({ ...current, [slot]: "" })),
    });
  }

  async function persistContent(next) {
    setSaving(true);
    setError(null);
    setErrorIssues([]);
    try {
      const prepared = markWeekMenusSaved({ client, previous: consultation, next });
      await onSave(prepared.consultation, { kind: "content" });
      setSavedChanges(prepared.changes);
      setNotice(reclaimsSource ? `${WEEK_MENUS_SAVED_NOTICE}. ${SOURCE_RETURN_NOTE}.` : WEEK_MENUS_SAVED_NOTICE);
      setMergeConflicts([]);
      setKeptDraftNotice(false);
      return true;
    } catch (saveError) {
      setError(saveError?.message || "Enregistrement impossible.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    if (!canEdit || (!dirty && !hasPendingRecipe)) return;
    let next = draft;
    // Une recette ouverte et modifiée est appliquée avant l'enregistrement,
    // jamais perdue en silence.
    if (hasPendingRecipe) {
      if (needsVaryingConfirmation(openTarget.slot)) {
        setPendingVarying({
          slot: openTarget.slot,
          operations: choiceOperations(openTarget.slot, openTarget.choice, openValues),
          // Confirmée depuis « Enregistrer » : l'enregistrement va jusqu'au bout.
          onDone: (confirmed) => {
            closeEditor();
            persistContent(confirmed);
          },
        });
        return;
      }
      const applied = runWeekMenusOps(
        { client, consultation: draft },
        choiceOperations(openTarget.slot, openTarget.choice, openValues),
      );
      if (!applied.ok) {
        showError(applied);
        return;
      }
      next = applied.consultation;
      setDraft(next);
      closeEditor();
    }
    await persistContent(next);
  }

  // Empreinte périmée (recettes modifiées ailleurs) : Anissa reprend
  // explicitement les menus tels qu'ils sont, même sans modification ici.
  async function retakeMenus() {
    if (!canEdit) return;
    if (dirty || hasPendingRecipe) {
      await save();
      return;
    }
    await persistContent(consultation);
  }

  // Confirmation explicite d'Anissa pour les exclusions écrites en prose :
  // enregistrée comme une relecture, jamais comme une validation du repas.
  async function confirmProse(slot) {
    if (!canEdit || dirty || hasPendingRecipe) return;
    const result = confirmProseExclusionsChecked({ client, consultation, weekMeals }, slot);
    if (!result.ok) {
      showError(result);
      return;
    }
    setSaving(true);
    try {
      await onSave(result.consultation, { kind: "validation" });
      setNotice("Vérification enregistrée. Le repas reste à valider.");
    } catch (saveError) {
      setError(saveError?.message || "Confirmation impossible.");
    } finally {
      setSaving(false);
    }
  }

  // Confirmation d'UN conflit signalé par précaution : tracée, liée au texte
  // exact et aux exclusions, jamais une validation du repas.
  async function confirmConflict(slot, conflictKey) {
    if (!canEdit || dirty || hasPendingRecipe) return;
    const result = confirmFoodConflict({ client, consultation, weekMeals }, slot, conflictKey);
    if (!result.ok) {
      showError(result);
      return;
    }
    if (result.unchanged) return;
    setSaving(true);
    try {
      await onSave(result.consultation, { kind: "validation" });
      setNotice("Compatibilité confirmée. Le repas reste à valider.");
    } catch (saveError) {
      setError(saveError?.message || "Confirmation impossible.");
    } finally {
      setSaving(false);
    }
  }

  async function validate(slot) {
    if (!canEdit || dirty || hasPendingRecipe) return;
    const result = validateSlot({ client, consultation, weekMeals }, slot);
    if (!result.ok) {
      showError(result);
      return;
    }
    setSaving(true);
    try {
      await onSave(result.consultation, { kind: "validation" });
      setNotice(`${model.slots.find((item) => item.slot === slot)?.label || "Repas"} validé.`);
      setSavedChanges([]);
    } catch (saveError) {
      setError(saveError?.message || "Validation impossible.");
    } finally {
      setSaving(false);
    }
  }

  const changesToShow = dirty ? pendingChanges : savedChanges;

  return (
    <section style={cardStyle} aria-labelledby="week-menus-title" className="week-menus">
      <style>{RESPONSIVE_CSS}</style>
      <header style={headerStyle}>
        <Icon type="calendar" size={18} style={{ color: "#d4c9a8" }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h4 id="week-menus-title" style={{ margin: 0, color: "#d4c9a8", fontSize: ".92rem", fontWeight: 600 }}>
            Semaine, menus & recettes
          </h4>
          <p style={{ margin: "2px 0 0", color: "#8a8a7a", fontSize: ".74rem", lineHeight: 1.5 }}>
            Un réservoir de choix par repas, partagé par les 4 semaines. Chaque semaine choisit son plat principal ; les autres choix restent ses alternatives.
          </p>
        </div>
        {dirty
          ? <StatusChip status={WEEK_MENU_STATUS.INCOMPLETE} label="Modifications non enregistrées" />
          : <StatusChip status={model.status} label={model.statusLabel} />}
      </header>

      <div style={{ padding: "14px 16px", display: "grid", gap: 14 }}>
        {authorityStale && (
          <div role="alert" style={{ border: "1px solid rgba(229, 184, 120, 0.32)", background: "rgba(229, 184, 120, 0.08)", borderRadius: 8, padding: "10px 12px", color: "#f2d39f", fontSize: ".78rem", lineHeight: 1.5 }}>
            <strong style={{ display: "block", marginBottom: 6 }}>{STALE_AUTHORITY_MESSAGE}</strong>
            <button type="button" disabled={!canEdit} onClick={retakeMenus} style={buttonStyle}>Reprendre ces menus</button>
          </div>
        )}
        {keptDraftNotice && dirty && (
          <p role="status" style={{ margin: 0, color: "#e5c878", fontSize: ".76rem" }}>{KEPT_DRAFT_MESSAGE}</p>
        )}
        {mergeConflicts.length > 0 && (
          <ul role="alert" style={{ margin: 0, paddingLeft: 18, color: "#f5c6c6", fontSize: ".76rem", lineHeight: 1.5 }}>
            {mergeConflicts.map((conflict) => <li key={conflict}>{conflict}</li>)}
          </ul>
        )}
        {model.slots.length === 0 ? (
          <p style={{ margin: 0, color: "#8a8a7a", fontSize: ".82rem" }}>Aucun repas dans la semaine pour l’instant.</p>
        ) : (
          <>
            <div role="tablist" aria-label="Semaines du cycle" className="week-menus__tabs">
              {model.weeks.map((item) => (
                <button
                  key={item.index}
                  type="button"
                  role="tab"
                  id={`week-menus-tab-${item.index}`}
                  aria-selected={activeWeek === item.index}
                  aria-controls="week-menus-panel"
                  className="week-menus__tab"
                  onClick={() => setActiveWeek(item.index)}
                  style={{
                    ...quietButtonStyle,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    borderColor: activeWeek === item.index ? "rgba(138,191,154,.45)" : "rgba(255,255,255,0.12)",
                    background: activeWeek === item.index ? "rgba(138,191,154,.11)" : "transparent",
                    color: activeWeek === item.index ? "#a8e0c0" : "#99998d",
                  }}
                >
                  <span>{item.label}</span>
                  <StatusChip status={item.status} label={item.statusLabel} />
                </button>
              ))}
            </div>

            <div role="tabpanel" id="week-menus-panel" aria-labelledby={`week-menus-tab-${week.index}`} style={{ display: "grid", gap: 12 }}>
              {week.isProposal && (
                <p style={{ margin: 0, color: "#e5c878", fontSize: ".76rem" }}>
                  Répartition des plats principaux : proposition, à valider.
                </p>
              )}
              <div className="week-menus__blocks">
                {model.slots.map((slotModel) => {
                  const mainId = week.mainBySlot[slotModel.slot];
                  const fromRotation = week.fromRotation[slotModel.slot];
                  const lowerLabel = slotModel.label.toLocaleLowerCase("fr");
                  const weekIssues = week.issues.filter((issue) => issue.includes(`— ${lowerLabel} :`));
                  const issues = [...slotModel.issues, ...weekIssues];
                  const alternativesCount = slotModel.choices.length - 1;
                  const canValidate = canEdit && !dirty && !hasPendingRecipe
                    && slotModel.issues.length === 0
                    && slotModel.status !== WEEK_MENU_STATUS.VALIDATED;
                  return (
                    <article key={slotModel.slot} style={blockStyle} aria-label={`${slotModel.label} — ${week.label}`}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                        <strong style={{ color: "#f0f0e8", fontSize: ".9rem" }}>{slotModel.label}</strong>
                        <StatusChip status={slotModel.status} label={slotModel.statusLabel} />
                      </div>
                      {slotModel.savedAt && slotModel.status === WEEK_MENU_STATUS.INCOMPLETE && (
                        <p style={{ margin: "0 0 8px", color: "#cfcfc4", fontSize: ".72rem" }}>
                          {WEEK_MENUS_SAVED_NOTICE} (une fois complet).
                        </p>
                      )}
                      {slotModel.variesByDay && !confirmedVarying.has(slotModel.slot) && pendingVarying?.slot !== slotModel.slot && (
                        <p style={{ margin: "0 0 8px", color: "#e5c878", fontSize: ".72rem" }}>
                          {VARYING_MESSAGE}
                        </p>
                      )}
                      {pendingVarying?.slot === slotModel.slot && (
                        <div role="alertdialog" aria-labelledby={`week-menus-varying-${slotModel.slot}`} style={{ border: "1px solid rgba(220, 180, 80, 0.30)", background: "rgba(220, 180, 80, 0.08)", borderRadius: 7, padding: 10, marginBottom: 10 }}>
                          <p id={`week-menus-varying-${slotModel.slot}`} style={{ margin: "0 0 8px", color: "#f2d39f", fontSize: ".76rem", lineHeight: 1.5 }}>
                            {VARYING_MESSAGE}
                          </p>
                          <div className="week-menus__actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => setPendingVarying(null)} style={quietButtonStyle}>Annuler</button>
                            <button type="button" onClick={confirmVarying} style={buttonStyle}>Confirmer pour les 7 jours</button>
                          </div>
                        </div>
                      )}

                      <label htmlFor={`week-menus-main-${week.index}-${slotModel.slot}`} style={labelStyle}>
                        Plat principal — {week.label}
                      </label>
                      <select
                        id={`week-menus-main-${week.index}-${slotModel.slot}`}
                        value={mainId || ""}
                        disabled={!canEdit}
                        onChange={(event) => {
                          if (event.target.value) run(slotModel.slot, [(state) => setWeekMain(state, week.index, slotModel.slot, event.target.value)]);
                        }}
                        style={{ ...inputStyle, background: "#20201f" }}
                      >
                        {!mainId && <option value="">À choisir</option>}
                        {slotModel.choices.map((choice) => (
                          <option key={choice.id} value={choice.id}>{choice.title}</option>
                        ))}
                      </select>
                      {!fromRotation && (
                        <p style={{ margin: "4px 0 0", color: "#8a8a7a", fontSize: ".7rem" }}>Proposition, à valider.</p>
                      )}

                      <p style={{ ...labelStyle, margin: "12px 0 6px" }}>
                        Réservoir — {alternativesCount} alternative{alternativesCount > 1 ? "s" : ""} (minimum {WEEK_MENUS_MIN_ALTERNATIVES})
                      </p>
                      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
                        {slotModel.choices.map((choice) => {
                          const open = openChoice === `${slotModel.slot}:${choice.id}`;
                          const isMain = choice.id === mainId;
                          return (
                            <li key={choice.id} style={{
                              border: `1px solid ${isMain ? "rgba(106, 191, 138, 0.28)" : "rgba(212, 201, 168, 0.10)"}`,
                              borderRadius: 7,
                              padding: "8px 10px",
                              background: isMain ? "rgba(106, 191, 138, 0.06)" : "transparent",
                            }}>
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                                <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                                  <span style={{ display: "block", color: isMain ? "#a8e0c0" : "#6a8a72", fontSize: ".64rem", fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase" }}>
                                    {isMain ? `Principal ${week.label.toLocaleLowerCase("fr")}` : "Alternative"}
                                  </span>
                                  <span style={{ display: "block", color: "#f0f0e8", fontSize: ".85rem", fontWeight: 600 }}>{choice.title}</span>
                                  {choice.hint && <span style={{ display: "block", color: "#c9c1aa", fontSize: ".74rem" }}>{choice.hint}</span>}
                                  <span style={{ display: "block", color: choice.recipeComplete ? "#8abf9a" : "#e5c878", fontSize: ".72rem", marginTop: 2 }}>
                                    {choice.hasRecipe
                                      ? `${choice.ingredientCount} ingrédient${choice.ingredientCount > 1 ? "s" : ""} · ${choice.stepCount} étape${choice.stepCount > 1 ? "s" : ""}`
                                      : "Recette à écrire"}
                                  </span>
                                </div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    aria-expanded={open}
                                    aria-label={`Modifier « ${choice.title} »`}
                                    disabled={!canEdit}
                                    onClick={() => openEditor(slotModel.slot, choice)}
                                    style={buttonStyle}
                                  ><span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon type="pencil" size={12} />Modifier</span></button>
                                  {choice.kind === "alternative" && (
                                    <button
                                      type="button"
                                      aria-label={`Retirer « ${choice.title} »`}
                                      disabled={!canEdit}
                                      onClick={() => run(slotModel.slot, [(state) => removeAlternative(state, slotModel.slot, choice.id)])}
                                      style={quietButtonStyle}
                                    >Retirer</button>
                                  )}
                                </div>
                              </div>
                              {open && openValues && (
                                <ChoiceEditor
                                  slot={slotModel.slot}
                                  choice={choice}
                                  values={openValues}
                                  onChange={setOpenValues}
                                  reusable={reusable}
                                  disabled={!canEdit}
                                  onApply={(values) => applyChoice(slotModel.slot, choice, values)}
                                  onReuse={(key) => run(slotModel.slot, [(state) => reuseRecipe(state, slotModel.slot, choice.id, key)], { onDone: closeEditor })}
                                  onClose={closeEditor}
                                />
                              )}
                            </li>
                          );
                        })}
                      </ul>

                      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                        <input
                          type="text"
                          aria-label={`Nouvelle alternative — ${slotModel.label}`}
                          placeholder="Titre de la nouvelle alternative"
                          value={newAlternative[slotModel.slot] || ""}
                          maxLength={WEEK_MENUS_LIMITS.title}
                          disabled={!canEdit}
                          onChange={(event) => setNewAlternative((current) => ({ ...current, [slotModel.slot]: event.target.value }))}
                          style={{ ...inputStyle, flex: "1 1 160px", width: "auto" }}
                        />
                        <button type="button" disabled={!canEdit} onClick={() => addAlt(slotModel.slot)} style={buttonStyle}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon type="plus" size={12} />Ajouter une alternative</span>
                        </button>
                      </div>

                      {issues.length > 0 && (
                        <ul style={{ margin: "10px 0 0", paddingLeft: 18, color: "#e5c878", fontSize: ".72rem", lineHeight: 1.5 }}>
                          {issues.map((issue) => <li key={issue}>{issue}</li>)}
                        </ul>
                      )}

                      {slotModel.foodConflicts.map((conflict) => (
                        <label key={conflict.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8, minHeight: 44, color: conflict.confirmed ? "#8abf9a" : "#cfcfc4", fontSize: ".74rem", lineHeight: 1.45 }}>
                          <input
                            type="checkbox"
                            checked={conflict.confirmed}
                            disabled={conflict.confirmed || !canEdit || dirty || hasPendingRecipe}
                            onChange={() => confirmConflict(slotModel.slot, conflict.key)}
                            style={{ marginTop: 2, minWidth: 18, minHeight: 18 }}
                          />
                          <span>{foodConflictConfirmationLabel(conflict)}{conflict.confirmed ? " — confirmé" : ""}</span>
                        </label>
                      ))}
                      {slotModel.proseExclusions.length > 0 && (
                        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, minHeight: 44, color: "#cfcfc4", fontSize: ".74rem", lineHeight: 1.45 }}>
                          <input
                            type="checkbox"
                            checked={false}
                            disabled={!canEdit || dirty || hasPendingRecipe}
                            onChange={() => confirmProse(slotModel.slot)}
                            style={{ marginTop: 2, minWidth: 18, minHeight: 18 }}
                          />
                          <span>{PROSE_EXCLUSIONS_CONFIRMATION}{dirty ? " (enregistre d’abord tes modifications)" : ""}</span>
                        </label>
                      )}
                      {slotModel.proseExclusionsChecked && (
                        <p style={{ margin: "8px 0 0", color: "#8abf9a", fontSize: ".72rem" }}>
                          Vérification des exclusions écrites en prose confirmée par Anissa pour ce contenu.
                        </p>
                      )}

                      <details style={{ marginTop: 10, color: "#cfcfc4", fontSize: ".72rem", lineHeight: 1.5 }}>
                        <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", color: "#d4c9a8" }}>
                          Contraintes de la cliente
                        </summary>
                        <dl style={{ margin: "4px 0 0", display: "grid", gridTemplateColumns: "minmax(0, 11rem) minmax(0, 1fr)", gap: "2px 10px" }}>
                          {constraints.map((item) => (
                            <div key={item.key} style={{ display: "contents" }}>
                              <dt style={{ color: "#8a8a7a" }}>{item.label}</dt>
                              <dd style={{ margin: 0 }}>{item.value}</dd>
                            </div>
                          ))}
                        </dl>
                        <p style={{ margin: "6px 0 0", color: "#8a8a7a" }}>{AUTOMATIC_CHECK_NOTE}</p>
                      </details>

                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                        <button
                          type="button"
                          disabled={!canValidate}
                          title={dirty ? "Enregistre d’abord tes modifications." : undefined}
                          onClick={() => validate(slotModel.slot)}
                          style={{ ...validateButtonStyle, opacity: canValidate ? 1 : 0.45, cursor: canValidate ? "pointer" : "not-allowed" }}
                        >{slotModel.status === WEEK_MENU_STATUS.VALIDATED ? "Repas validé" : "Valider ce repas"}</button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {changesToShow.length > 0 && (
              <div role="status" style={{ borderRadius: 8, border: "1px solid rgba(212, 201, 168, 0.18)", padding: "10px 12px", color: "#cfcfc4", fontSize: ".76rem", lineHeight: 1.5 }}>
                <strong style={{ display: "block", color: "#d4c9a8", marginBottom: 4 }}>À revérifier</strong>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {changesToShow.map((change) => <li key={change.slot}>{describeWeekMenuChange(change)}</li>)}
                </ul>
                {typeof onOpenRotation === "function" && (
                  <button type="button" onClick={onOpenRotation} style={{ ...quietButtonStyle, marginTop: 8 }}>Voir la rotation 4 semaines</button>
                )}
              </div>
            )}
          </>
        )}

        {(error || notice) && (
          <div role={error ? "alert" : "status"} style={{ color: error ? "#f5c6c6" : "#8abf9a", fontSize: ".78rem", display: "grid", gap: 4 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <Icon type={error ? "warning" : "check"} size={12} />{error || notice}
            </span>
            {error && errorIssues.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errorIssues.map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            )}
          </div>
        )}

        {reclaimsSource && (dirty || hasPendingRecipe) && (
          <p role="status" style={{ margin: 0, color: "#e5c878", fontSize: ".74rem", textAlign: "right" }}>
            À l’enregistrement : {SOURCE_RETURN_NOTE.charAt(0).toLocaleLowerCase("fr")}{SOURCE_RETURN_NOTE.slice(1)} du Dossier application.
          </p>
        )}
        {hasPendingRecipe && (
          <p role="status" style={{ margin: 0, color: "#cfcfc4", fontSize: ".74rem", textAlign: "right" }}>{PENDING_RECIPE_MESSAGE}</p>
        )}
        <div className="week-menus__actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={(!dirty && !hasPendingRecipe) || saving}
            onClick={() => { setDraft(consultation); closeEditor(); setKeptDraftNotice(false); setError(null); setErrorIssues([]); }}
            style={{ ...quietButtonStyle, opacity: dirty || hasPendingRecipe ? 1 : 0.45 }}
          >Annuler les modifications</button>
          <button
            type="button"
            disabled={!canEdit || (!dirty && !hasPendingRecipe)}
            onClick={save}
            style={{ ...validateButtonStyle, opacity: canEdit && (dirty || hasPendingRecipe) ? 1 : 0.45, cursor: canEdit && (dirty || hasPendingRecipe) ? "pointer" : "not-allowed" }}
          >{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </section>
  );
}
